import { useEffect, useRef, useState } from "react";

import { obterClips } from "../api/client";
import type { ClipPoint } from "../types";

export interface Clips {
  clips: ClipPoint[];
  erro: string | null;
  aRecarregar: boolean;
  truncado: boolean;
}

const VAZIO: Clips = { clips: [], erro: null, aRecarregar: false, truncado: false };

/**
 * Os clips de vídeo de uma boia, para o intervalo escolhido.
 *
 * Pedido separado do `useHistory` de propósito: os clips só existem para a
 * boia do painel (comparar vídeo entre boias não é uma leitura útil), e um
 * erro aqui — retenção que expulsou tudo, endpoint novo num backend velho —
 * não pode arrastar os gráficos com ele.
 *
 * O mesmo padrão de recarga: guarda-se a lista anterior e assinala-se
 * `aRecarregar`, em vez de piscar para o vazio a cada mudança de intervalo.
 */
export function useClips(code: string, de: Date, ate: Date): Clips {
  const [dados, setDados] = useState<Clips>(VAZIO);
  const abortar = useRef<AbortController | null>(null);

  const deMs = de.getTime();
  const ateMs = ate.getTime();

  useEffect(() => {
    abortar.current?.abort();
    const controlo = new AbortController();
    abortar.current = controlo;

    setDados((a) => ({ ...a, aRecarregar: true }));

    void (async () => {
      try {
        const resposta = await obterClips(code, new Date(deMs), new Date(ateMs), controlo.signal);
        setDados({
          clips: resposta.itens,
          erro: null,
          aRecarregar: false,
          truncado: resposta.truncado,
        });
      } catch (erro) {
        if (controlo.signal.aborted) return;
        setDados((a) => ({
          ...a,
          erro: erro instanceof Error ? erro.message : "Falha a ler os clips",
          aRecarregar: false,
        }));
      }
    })();

    return () => controlo.abort();
  }, [code, deMs, ateMs]);

  return dados;
}
